import {
  type CopilotTelemetryFields,
  logCopilotTelemetry,
  type StaticEventLabel,
} from '@/app-shell/copilot/telemetry';
import { isTauri } from '@/tauri/utils';
import { useEffect, useSyncExternalStore } from 'react';
import { isCopilotDebugAllowed } from './debug-policy';

/**
 * Copilot action log — a cross-window debug trail for the pre-chart loop.
 *
 * Every step (chip click → intent → import → batch capture → orchestrator →
 * agent run → context write) logs here so "nothing happened" is diagnosable
 * from the overlay's Debug ▸ AX Help tab. Entries are broadcast over a
 * Tauri event so the copilot overlay sees actions taken in the main window
 * and the RPA agent window. Dev/staging tooling: entries can carry patient
 * names (same-device IPC only, like `copilot:active-note`).
 */
export const COPILOT_ACTION_LOG_EVENT = 'copilot:action-log';

export interface CopilotActionEntry {
  ts: number;
  /** Which window logged it: 'main' | 'copilot' | 'agent'. */
  source: string;
  event: string;
  detail?: string;
}

const MAX_ENTRIES = 200;

let entries: readonly CopilotActionEntry[] = [];
const listeners = new Set<() => void>();

const append = (entry: CopilotActionEntry) => {
  entries = [...entries.slice(-(MAX_ENTRIES - 1)), entry];
  listeners.forEach((notify) => notify());
};

const sourceLabel = (): string => {
  if (typeof window === 'undefined') return 'main';
  const path = window.location.pathname;
  if (path.startsWith('/copilot-overlay')) return 'copilot';
  if (path.startsWith('/overlay')) return 'agent';
  return 'main';
};

/**
 * Log a copilot action. Always mirrors to the console; in Tauri also
 * broadcasts so every window's log converges. Never throws.
 *
 * `event` must be a static, PHI-free label — it is forwarded to Datadog, and
 * the `StaticEventLabel` constraint rejects dynamically-built strings at
 * compile time. `detail` may carry patient/note text and never leaves the
 * device. `fields` are PHI-safe structured attributes (outcome tokens, reasons,
 * error codes, counts, durations) forwarded to Datadog alongside the event so
 * a failure is debuggable remotely — never put note/patient text there.
 */
export const logCopilotAction = <E extends string>(
  event: StaticEventLabel<E>,
  detail?: string,
  fields?: CopilotTelemetryFields
): void => {
  const entry: CopilotActionEntry = {
    ts: Date.now(),
    source: sourceLabel(),
    event,
    ...(detail ? { detail } : {}),
  };
  console.info(`[copilot] ${entry.event}`, entry.detail ?? '');
  logCopilotTelemetry(event, { copilot_source: entry.source, ...fields });
  append(entry);
  // Cross-window buffer is debug-only (may carry patient names). Prod never
  // opens the panel — skip the remote listener there.
  if (isCopilotDebugAllowed()) {
    ensureRemoteSubscription();
  }
  if (!isTauri()) return;
  void import('@tauri-apps/api/event')
    .then(({ emit }) => emit<CopilotActionEntry>(COPILOT_ACTION_LOG_EVENT, entry))
    .catch(() => {});
};

export const clearCopilotActionLog = (): void => {
  entries = [];
  listeners.forEach((notify) => notify());
};

const subscribe = (notify: () => void): (() => void) => {
  listeners.add(notify);
  return () => listeners.delete(notify);
};

const EMPTY: readonly CopilotActionEntry[] = [];
const getSnapshot = (): readonly CopilotActionEntry[] => entries;
const getServerSnapshot = (): readonly CopilotActionEntry[] => EMPTY;

/** Attach the cross-window listener once per window. */
let remoteSubscribed = false;
const ensureRemoteSubscription = () => {
  if (remoteSubscribed || !isTauri()) return;
  remoteSubscribed = true;
  const ownSource = sourceLabel();
  void import('@tauri-apps/api/event')
    .then(({ listen }) =>
      listen<CopilotActionEntry>(COPILOT_ACTION_LOG_EVENT, ({ payload }) => {
        // Own entries were already appended locally at log time.
        if (payload.source !== ownSource) append(payload);
      })
    )
    .catch(() => {
      remoteSubscribed = false;
    });
};

/** Live view of the action log (newest last). */
export const useCopilotActionLog = (): readonly CopilotActionEntry[] => {
  useEffect(() => {
    if (isCopilotDebugAllowed()) ensureRemoteSubscription();
  }, []);
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
